import React, { useState } from 'react';
import { Card, List, Tag, Button, Input, Skeleton, message } from 'antd';
import {
  EditOutlined,
  PlusOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import type { MenuCategory } from '../../../types/menu';

export interface CategoryListProps {
  categories: MenuCategory[];
  selectedCategoryId: string | null;
  onSelectCategory: (id: string | null) => void;
  onAddCategory: () => void;
  onEditCategory: (
    category: MenuCategory,
    newName: string,
    adminNote: string
  ) => Promise<void>;
  onToggleCategoryActive: (
    category: MenuCategory,
    adminNote: string
  ) => Promise<void>;
  onConfirmWithNote: (
    action: string,
    fn: (note: string) => Promise<void>
  ) => void;
  loading?: boolean;
}

export const CategoryList: React.FC<CategoryListProps> = ({
  categories,
  selectedCategoryId,
  onSelectCategory,
  onAddCategory,
  onEditCategory,
  onToggleCategoryActive,
  onConfirmWithNote,
  loading = false,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const sortedCategories = [...categories].sort(
    (a, b) => a.displayOrder - b.displayOrder
  );

  const handleStartEdit = (cat: MenuCategory) => {
    setEditingId(cat.id);
    setEditName(cat.name);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleSaveEdit = (cat: MenuCategory) => {
    const newName = editName.trim();
    if (newName !== cat.name) {
      onConfirmWithNote('Rename category', async note => {
        await onEditCategory(cat, newName, note);
        handleCancelEdit();
      });
    } else {
      handleCancelEdit();
    }
  };

  if (loading) {
    return (
      <Card className="merchant-menu-category-card" title="Menu Categories">
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  return (
    <Card
      className="merchant-menu-category-card"
      title=""
      extra={
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={onAddCategory}
        >
          Thêm danh mục
        </Button>
      }
    >
      <List
        className="merchant-menu-category-list"
        size="small"
        dataSource={[
          {
            id: null,
            name: 'Tất cả món ăn',
            isActive: true,
            displayOrder: -1,
            dishCount: 0,
          },
          ...sortedCategories,
        ]}
        renderItem={item => {
          const isAll = item.id === null;
          const isSelected =
            selectedCategoryId === item.id ||
            (isAll && selectedCategoryId === '__all__');
          const isEditing = editingId === item.id;

          return (
            <List.Item
              className={`merchant-menu-category-item ${
                isSelected ? 'merchant-menu-category-item-selected' : ''
              }`}
              onClick={() => !isAll && onSelectCategory(item.id)}
              style={isAll ? { cursor: 'pointer' } : undefined}
            >
              {isAll ? (
                <div
                  className="merchant-menu-category-item-content"
                  onClick={() => onSelectCategory('__all__')}
                >
                  <span className="merchant-menu-category-item-name">
                    Tất cả món ăn
                  </span>
                </div>
              ) : (
                <div className="merchant-menu-category-item-content">
                  {isEditing ? (
                    <div
                      className="merchant-menu-category-edit"
                      onClick={e => e.stopPropagation()}
                    >
                      <Input
                        size="small"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onPressEnter={() => handleSaveEdit(item)}
                        autoFocus
                      />
                      <div className="merchant-menu-category-edit-actions">
                        <Button
                          type="link"
                          size="small"
                          onClick={() => handleSaveEdit(item)}
                        >
                          Save
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          onClick={handleCancelEdit}
                        >
                          Discard
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className="merchant-menu-category-item-name">
                        {item.name}
                      </span>
                      <span className="merchant-menu-category-item-count">
                        {item.dishCount}
                      </span>
                      <Tag
                        color={item.isActive ? 'green' : 'default'}
                        className="merchant-menu-category-tag"
                      >
                        {item.isActive ? 'Active' : 'Hidden'}
                      </Tag>
                      <div className="merchant-menu-category-actions">
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={e => {
                            e.stopPropagation();
                            handleStartEdit(item);
                          }}
                        />
                        <Button
                          type="text"
                          size="small"
                          icon={
                            item.isActive ? (
                              <EyeInvisibleOutlined />
                            ) : (
                              <EyeOutlined />
                            )
                          }
                          onClick={e => {
                            e.stopPropagation();
                            onConfirmWithNote(
                              item.isActive ? 'Hide category' : 'Show category',
                              note => onToggleCategoryActive(item, note)
                            );
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </List.Item>
          );
        }}
      />
    </Card>
  );
};
